<template>

  <div
    v-if="router !== '/login'"
    :class="containerClass"
    @click="onDocumentClick"
    ref="printMe"
  >
    <AppTopBar
      :topbarMenuActive="topbarMenuActive"
      :activeTopbarItem="activeTopbarItem"
      :horizontal="isHorizontal()"
      :profileMode="profileMode"
      @menubutton-click="onMenuButtonClick"
      @topbar-menubutton-click="onTopbarMenuButtonClick"
      @topbar-item-click="onTopbarItemClick"
      @processAlert="goto"
      @viewAllAlerts="viewAllAlerts"
      @screenshot="takeScreenshot"
      @editAllFavourities="editAllFavourities"
    ></AppTopBar>

    <transition name="layout-menu-container">
      <div
        class="layout-menu-container"
        @click="onMenuClick"
        v-show="isMenuVisible()"
      >
        <div class="layout-menu-logo">
          <button class="p-link">
            <img
              id="layout-menu-logo1"
              src="../public/mbp_logo.png"
              alt="MBP Group Synergy System"
            />
          </button>
        </div>
        <div class="layout-menu-wrapper">
          <div class="menu-scroll-content">
            <AppInlineProfile
              v-if="displayInlineProfile"
              @profile-click="onProfileClick"
              :expanded="profileExpanded"
            ></AppInlineProfile>
            <AppMenu
              :key="AppMenuKey"
              :model="grouped ? menuGrouped : menuUngrouped"
              :layoutMode="layoutMode"
              :active="menuActive"
              @menuitem-click="onMenuItemClick"
              @root-menuitem-click="onRootMenuItemClick"
            ></AppMenu>
          </div>
        </div>
      </div>
    </transition>

    <div class="layout-main">
      <AppBreadcrumb></AppBreadcrumb>
      
      <Toast position="bottom-left">
          <template #message="slotProps">
              <div class="flex flex-column">
                  <div class="text-center">
                      <i class="pi pi-exclamation-triangle" style="font-size: 3rem"></i>
                      <h4>{{slotProps.message.summary}}</h4>
                      <p>{{slotProps.message.detail}}</p>
                  </div>
                  <div class="grid p-fluid">
                      <div class="col-6">
                          <Button class="p-button-success" label="Yes" @click="onConfirm" />
                      </div>
                      <div class="col-6">
                          <Button class="p-button-secondary" label="No" @click="onReject" />
                      </div>
                  </div>
              </div>
          </template>
      </Toast>

      <div class="layout-content" ref="div1" id="layout-content">
        <router-view />
      </div>

      <AppFooter />
    </div>

    <AppConfig
      :layoutMode="layoutMode"
      @layout-change="onLayoutChange"
      :darkMenu="darkMenu"
      @menu-color-change="onMenuColorChange"
      :profileMode="profileMode"
      @profile-mode-change="onProfileModeChange"
      :grouped="grouped"
      @menu-type-change="onMenuTypeChange"
    ></AppConfig>

    <div v-if="staticMenuMobileActive" class="layout-mask"></div>

    <div
      v-if="$store.getters.getUploading"
      id="progressDiv"
    >
      <div id="progressDetails">
        <ProgressBar :value="$store.getters.getPercentageUploaded" :showValue="false" style="height: 1.5em"/>
        <span>{{$store.getters.getProgressMsg }} {{ $store.getters.getPercentageUploaded }}%</span>
      </div>
    </div>

    <Dialog
      :visible.sync="loginDialog"
      :style="{ width: '500px', height: '353px' }"
      header="Your session has expired, please login again."
      :modal="true"
      class="p-fluid"
      :closable="false"
    >
      <LoginForm 
        @passwordForgotten="forgottenPassword()"
      />
      <br />
      <br />
    </Dialog>

    <ForgetPassword 
      :showDialog="forgetPasswordDialog"
      :resetDialog="resetForgetPasswordDialog"
      @close="forgetPasswordDialog = false"
    />

    <SupportCaseForm :supportCaseDialog="supportCaseDialog"
    :attachment="attachment"
    @close="supportCaseDialog=false"
    />
    
    <component
      :key="activityFormKey"
      :is="activityForm"
      :activityForm="activityDialog"
      mode="auto"
      :activityToEdit="activity"
      :disabledArr="disabledArr"
      @close="activityDialog = false"
    />

    <component
      :is="noteForm"
      :reload="reloadNoteForm"
      :noteDialog="noteDialog"
      :noteToEdit="note"
      :disabledArray="disabledArr"
      @noteSaved="noteSaved"
      @close="noteDialog = false"
    />

    <component
      :is="customerForm"
      :customerToEdit="customer"
      :customerDialog="customerDialog"
      :disabledArr="disabledArr"
      @close="customerDialog = false"
    />

    <component
      :is="quotationForm"
      :quotationDialog="quotationDialog"
      :active="activeQuote"
      :quoteToEdit="quotation"
      :opportunity="opportunity"
      :disabledArr="disabledArr"
      @close="activeQuote = false; quotationDialog = false;"
    />

    <Dialog
      :visible.sync="alertsDialog"
      :style="{ width: '80%' }"
      header="Alerts"
      :modal="true"
      class="p-fluid"
    >
      <div style="min-height: 500px;">
        <component
          :is="alertsTable"
          :alerts="alerts"
          :loading="loadingAlerts"
          :processing="processing"
          @processAlert="goto"
          @updateAlert="updateAlert"
          @reload="viewAllAlerts"
        />
      </div>
    </Dialog>

    <Dialog
      :visible.sync="filtersDialog"
      :style="{ width: '80%' }"
      header="Filters"
      :modal="true"
      class="p-fluid"
    >
      <div style="min-height: 500px;">
        <component
          :is="filtersTable"
          :loading="false"
          :savedFilters="favouriteFilters"
          @updateFilter="updateFilter"
        />
      </div>
    </Dialog>

    <!-- D11: after a new quote version is booked, offer to restart follow-ups stopped by hand -->
    <FollowupRevisionDialog />

    <!-- Batch 5: "Please check this value" before a quote with an unusual value is saved -->
    <AnomalyConfirmDialog />

  </div>
  <div v-else>
    <router-view />
  </div>
</template>

<script>
import QuotationService from "@/service/QuotationService";
import UserService from '@/service/UserService.js'
import FiltersService from "@/service/FiltersSerice"
import ActivityService from '@/service/ActivityService.js'
import CustomerService from '@/service/CustomerService.js'
import AlertService from '@/service/AlertService.js'
import ReportsService from '@/service/ReportsService.js'
import SettingService from '@/service/SettingService.js'
import RoleService from "@/service/RoleService";
import { shouldSetUpIdleLogout } from "@/idleLogout";
import AttachmentService from "@/service/AttachmentService";


import AppTopBar from "./AppTopbar.vue";
import AppInlineProfile from "./AppInlineProfile.vue";
import AppMenu from "./AppMenu.vue";
import AppConfig from "./AppConfig.vue";
import AppBreadcrumb from "./AppBreadcrumb.vue";
import AppFooter from "./AppFooter.vue";
import EventBus from "./event-bus";
import { firstLoadModules } from "./listViewRegistry.js";

import LoginForm from './mbp/auth/LoginForm'
import ForgetPassword from './mbp/auth/ForgotPassword'
import SupportCaseForm from './mbp/supportCase/SupportCaseForm'
import ActivityForm from "@/mbp/universalComponents/activity/ActivityForm.vue";
import NoteForm from '@/mbp/universalComponents/note/NoteForm'
import AlertsTable from '@/mbp/universalComponents/alerts/AlertsTable'
import FollowupRevisionDialog from '@/mbp/universalComponents/quotation/FollowupRevisionDialog.vue'
import AnomalyConfirmDialog from '@/mbp/universalComponents/quotation/AnomalyConfirmDialog.vue'
import FiltersTable from '@/mbp/universalComponents/filter/FiltersTable'
import customerForm from '@/mbp/universalComponents/customer/customerForm'
import QuotationForm from '@/mbp/universalComponents/quotation/QuotationForm'

export default {
  data() {
    return {
      roleService: new RoleService(),
      attachmentService: new AttachmentService(),
      quotationService: new QuotationService(),
      userService: new UserService(),
      filtersService: new FiltersService(),
      activityService: new ActivityService(),
      customerService: new CustomerService(),
		  alertService: new AlertService(),
		  reportsService: new ReportsService(),
		  settingService: new SettingService(),

      layoutMode: "static", // save
      staticMenuDesktopInactive: false,
      staticMenuMobileActive: false,
      overlayMenuActive: false,
      topbarMenuActive: false,
      activeTopbarItem: null,
      menuActive: false,
      darkMenu: true, // save
      profileMode: "inline", // save
      profileExpanded: false,
      grouped: true, // save
      menuGrouped: Array,
      menuUngrouped: Array,
      AppMenuKey: 0,
      login: {},
      
      supportCaseDialog: false,
      attachment: null,

      processing: false,
      customerDialog: false,
      customer: {},
      unRequestedCustomer: true,
      quotation: {}  ,
      quotationDialog: false,
      opportunity: {},
      activeQuote: false,
      activityDialog: false,
      disabledArr: [],
      activity: {},
      activityFormKey1: 0,
      noteDialog: false,
      note: {},
      reloadNoteForm: 0,
      unRequestedActivity: true,
      unRequestedNote: true,
      alertsDialog: false,
      unRequestedAlertsTable: true,
      alerts: [],
      requestingAlerts: false,
      forgetPasswordDialog: false,
      resetForgetPasswordDialog: 0,
      filtersDialog: false,
      unRequestedFiltersTable: true,
      favouriteFilters: [],
      

      allReports: [],
      divWidth: null,
      divHeight: null,

      // Inactivity timeout. Only user input events (mouse/key/scroll/touch)
      // reset this — background API traffic does not count as activity.
      inactivityTimeout: null,
      inactivityDuration: 3600000, // 60 minutes
      lastActivityTime: null,

      extendableOptions: [
        {
          label: "Customer Settings",
          icon: "pi pi-fw pi pi-cog",
          items: [
            {
              to: "/customerSetting/Types",
              label: "Customer Types",
              class: "submenu-item"
            },
            {
              to: "/customerSetting/Industry",
              label: "Industry Types",
              class: "submenu-item"
            },
            {
              to: "/customerSetting/PostalAreas",
              label: "Postal Areas",
              class: "submenu-item"
            },
            {
              to: "/customerSetting/Regions",
              label: "Regions",
              icon: "pi pi-fw pi-globe",
              class: "submenu-item"
            },
            {
              to: "/customerSetting/SalesExecutives",
              label: "Sales Executives",
              icon: "pi pi-fw pi-eye",
              class: "submenu-item"
            },
            {
              to: "/customerSetting/JobTitles",
              label: "Contact Job Titles",
              class: "submenu-item"
            },
            {
              to: "/customerSetting/Salutations",
              label: "Salutations",
              class: "submenu-item"
            },
            {
              to: "/customerSetting/citiesTowns",
              label: "Cities/Towns",
              class: "submenu-item"
            },
            {
              to: "/customerSetting/jobFunctions",
              label: "Job Functions",
              class: "submenu-item"
            },
            {
              to: "/customerSetting/Rebates",
              label: "Rebates",
              class: "submenu-item"
            }
          ],
        },
        {
          label: "Enquiry Settings",
          icon: "pi pi-fw pi-cog",
          items: [
            {
              to: "/opportunitySetting/Status",
              label: "Enquiry Status",
              class: "submenu-item"
            },
            {
              to: "/opportunitySetting/ProjectTypes",
              label: "Project Types",
              class: "submenu-item"
            },
            {
              to: "/opportunitySetting/ProductTypes",
              label: "Product Types",
              class: "submenu-item"
            },
            {
              to: "/opportunitySetting/QuotationGrades",
              label: "Quotation Grades",
              class: "submenu-item"
            }
          ],
        },
        {
          label: "Quotation Settings",
          icon: "pi pi-fw pi-cog",
          items: [
            {
              to: "/quoteSetting/QuoteTypes",
              label: "Quote Types",
              class: "submenu-item"
            },
            {
              to: "/quoteSetting/QuoteStatus",
              label: "Quote Status",
              class: "submenu-item"
            },
            {
              to: "/quoteSetting/LostQuoteNotes",
              label: "Lost Quote Notes",
              class: "submenu-item"
            },
            {
              to: "/quoteSetting/Manufacturers",
              label: "Manufacturers",
              class: "submenu-item"
            }
          ],
        },
        {
          label: "Task & Event Settings",
          icon: "pi pi-fw pi-cog",
          items: [
            {
              to: "/activitySetting/ActivityResults",
              label: "Results",
              class: "submenu-item"
            },
            {
              to: "/activitySetting/ActivityStatus",
              label: "Statuses",
              class: "submenu-item"
            },
            {
              to: "/activitySetting/ActivityTypes",
              label: "Types",
              class: "submenu-item"
            },
            {
              to: "/activitySetting/ReminderTypes",
              label: "Reminder Types",
              class: "submenu-item"
            }
          ],
        },
        {
          label: "Follow-up Settings",
          icon: "pi pi-fw pi-cog",
          items: [
            {
              to: "/followupSetting/Settings",
              label: "Follow-up Emails",
              class: "submenu-item"
            },
            {
              to: "/followupSetting/Steps",
              label: "Follow-up Steps",
              class: "submenu-item"
            },
            // Bank Holidays, Enrol Quotes and Approval Feedback left the menu (Mohammed 04/10/2026):
            // bank holidays are a tab of Follow-up Emails, quotes are enrolled by the go-live script,
            // and the approval answers are still recorded. Their routes and pages stay.
          ],
        },
        {
          // Batch 5. Admin only: setupMenu() leaves adminOnly groups out for every other role.
          label: "Warnings & Approvals",
          icon: "pi pi-fw pi-cog",
          adminOnly: true,
          items: [
            {
              to: "/warningsApprovals/Settings",
              label: "Settings",
              class: "submenu-item"
            },
            {
              to: "/warningsApprovals/Log",
              label: "Warning Log",
              class: "submenu-item"
            }
          ],
        },
        {
          label: "Global Settings",
          icon: "pi pi-fw pi pi-cog",
          items: [
            {
              label: "Branches",
              icon: "pi pi-fw pi-check-square",
              to: "/branches",
              class: "submenu-item"
            },
            {
              label: "Tags",
              icon: "pi pi-fw pi-tags",
              to: "/tags",
              class: "submenu-item"
            },
            {
              to: "/setting/mbpDepartments",
              label: "MBP Departments",
              class: "submenu-item"
            },
            {
              to: "/setting/dashboard",
              label: "Dashboard Settings",
              class: "submenu-item"
            }
          ],
        },
      ],

      reportGroups: []
    };
  },
  created: function () {
    
    // 
    // 
    // this.$store.dispatch('auth/setMyProfile', this.$store.getters['auth/getMyProfile'])
    // this.$store.dispatch('auth/setMyToken', this.$store.getters['auth/getMyToken'])

    this.getReports()

    this.setupMenu()

    this.setupFirstLoad()
    this.setupProfile(this.profile)
    // A full page load with a stored profile never fires the profile watcher, so the
    // permissions (kept in memory only, not in localStorage) must be fetched here too.
    if (this.profile && this.profile.id) {
      this.getUserPermissions()
    }
    this.getCurrUserFavFilters()
    
    window.addEventListener("resize", this.windowResizedHandler);

    // Setup inactivity timeout tracking (60-minute idle auto-logout). LIVE always; the
    // TEST build skips it - see src/idleLogout.js.
    if (shouldSetUpIdleLogout()) {
      this.setupInactivityTimeout();
    }

    // this.watchScrolling()
  },
  mounted() {
    
    this.windowResizedHandler()

    this.getPreLoadedEps()
  },
  destroyed() {
    window.removeEventListener("resize", this.windowResizedHandler);
    this.cleanupInactivityTimeout();
  },
  watch: {
    myPermissions(val) {
      if (val && val[0]) {
        this.setupMenu();
      }
    },
    router(val) {
      setTimeout(() => {
        
        var found = null
      
        for (let i = 0; i < this.extendableOptions.length; i ++) {
          for (let j = 0; j < this.extendableOptions[i].items.length; j ++) {
            if (this.extendableOptions[i].items[j].to === val) {
              found = this.extendableOptions[i]
              break
            }
          }
        }
      
        if (!found) {
          for (let i = 0; i < this.reportGroups.length; i ++) {
            for (let j = 0; j < this.reportGroups[i].items.length; j ++) {
              if (this.reportGroups[i].items[j].to === val) {
                found = this.reportGroups[i]
                break
              }
            }
          }
        }

        if (found && found.label) {
          let el = this.$el.getElementsByClassName(found.label)[0]
          if(el && el.children[0]) {
            el.children[0].click()
          }
        }
        
      }, 10)
    },
    staticMenuDesktopInactive(val) {
      this.$store.dispatch('setStaticMenuDesktopInactive', val)
    },
    route(val) {
      if (val === 'dashboard') {
        this.profileExpanded = false
      }
    },
    profile(val) {
      
      if (val && val.id) {
        this.getReports()
        this.setupMenu()
        this.setupProfile(val)
        this.getUserPermissions()
        
        // Setup inactivity timeout when user logs in (not on TEST, see created())
        if (shouldSetUpIdleLogout()) {
          this.setupInactivityTimeout()
        }
      } else {
        // Clean up when user logs out
        this.cleanupInactivityTimeout()
      }
    },
    $route() {
      this.menuActive = false;
      this.$toast.removeAllGroups();
      this.upatdeAccounts();
    },
    divWidth(val) {
      // 
      this.$store.dispatch('setAppWindowWidth', val)
    },
    divHeight(val) {
      // 
      this.$store.dispatch('setAppWindowHeight', val)
    }
  },
  methods: {
    getUserPermissions() {
      if (!this.profile || !this.profile.id) {
        return Promise.resolve()
      }
      // The status lets a page that waits for permissions tell "still loading" from
      // "loaded, none granted" and "failed", instead of spinning for ever.
      this.$store.dispatch('auth/setUserPermissionsStatus', 'loading')
      return this.roleService
        .getUserPermissions(this.profile.id)
        .then((data) => {
          this.$store.dispatch(`auth/setUserPermissions`, data || [])
          this.$store.dispatch('auth/setUserPermissionsStatus', 'loaded')
        })
        .catch(error => {
          this.$store.dispatch('auth/setUserPermissionsStatus', 'error')
          this.$store.dispatch('addErrorMessage', {
            severity: 'error',
            content: 'Some data was not loaded from "getUserPermissions"'
          })
        })
    },
    // Inactivity timeout methods
    setupInactivityTimeout() {
      // Only setup if user is logged in
      if (this.profile && this.profile.id) {
        this.lastActivityTime = new Date().getTime();
        this.resetInactivityTimer();
        
        // Track user activity
        window.addEventListener('mousemove', this.handleUserActivity);
        window.addEventListener('mousedown', this.handleUserActivity);
        window.addEventListener('keypress', this.handleUserActivity);
        window.addEventListener('scroll', this.handleUserActivity);
        window.addEventListener('touchstart', this.handleUserActivity);
      }
    },
    handleUserActivity() {
      // Update last activity time
      this.lastActivityTime = new Date().getTime();
      // Reset the inactivity timer
      this.resetInactivityTimer();
    },
    resetInactivityTimer() {
      // Clear existing timeout
      if (this.inactivityTimeout) {
        clearTimeout(this.inactivityTimeout);
      }
      
      // Set new timeout
      this.inactivityTimeout = setTimeout(() => {
        this.handleInactivityLogout();
      }, this.inactivityDuration);
    },
    handleInactivityLogout() {
      // Check if user is still logged in
      if (this.profile && this.profile.id && this.$store.getters['auth/getMyToken']) {
        // Clear user session
        this.$store.dispatch('auth/setMyProfile', null);
        this.$store.dispatch('auth/setMyToken', null);
        this.$store.dispatch('auth/setMyRefreshToken', null);
        
        // Show session expired dialog
        this.$store.dispatch('setReLoginDialog', true);
        
        // Clean up activity listeners
        this.cleanupInactivityTimeout();
      }
    },
    cleanupInactivityTimeout() {
      // Clear timeout
      if (this.inactivityTimeout) {
        clearTimeout(this.inactivityTimeout);
        this.inactivityTimeout = null;
      }
      
      // Remove event listeners
      window.removeEventListener('mousemove', this.handleUserActivity);
      window.removeEventListener('mousedown', this.handleUserActivity);
      window.removeEventListener('keypress', this.handleUserActivity);
      window.removeEventListener('scroll', this.handleUserActivity);
      window.removeEventListener('touchstart', this.handleUserActivity);
    },
    noteSaved() {
      this.noteDialog = false;
      this.note = {};
    },
    watchScrolling() {
      window.document.body.onscroll = () => {
        if (this.$route.name === 'reports') {
          let a = document.getElementsByClassName('p-datatable-wrapper')
          // 
          if (a && a.length) {
            if (
              a[a.length - 1].children
              && a[a.length - 1].children.length
              && a[a.length - 1].children[a[a.length - 1].children.length - 1]
              && a[a.length - 1].children[a[a.length - 1].children.length - 1].children
              ) {
              // 
              // 
              // 
              // 
              
              let b = a[a.length - 1].children[a[a.length - 1].children.length - 1].children[0]

              if (b) {
                if (a[a.length - 1].children[a[a.length - 1].children.length - 1].getBoundingClientRect().top <= 60) {
                  b.style.position = 'fixed'
                  b.style.top = '60px'
                } else {
                  b.style.position = 'static'
                  b.style.top = 'auto'
                }
              }
            }

          }
        }
      }
    },
    windowResizedHandler(e) {
      // your code for handling resize...
      if (this.$refs.div1) {
        this.divWidth = this.$refs.div1.clientWidth
        this.divHeight = this.$refs.div1.clientHeight
      }
    },
    getReports() {
      if (this.$store.getters['auth/getMyToken']) {
        this.reportsService
          .dbreports()
          .then(data => {
            
            this.allReports = data
            this.$store.dispatch('reports/setAllReports', data)
            
            this.setupMenu()
          })
          .catch(error => {
            this.$store.dispatch('addErrorMessage', {
              severity: 'error',
              content: 'Some data was not loaded from "dbreports"'
            })
          })
      }
    },
    updateFilter(filter) {
      let filterIndex = this.favouriteFilters.findIndex(f => f.id === filter.id)
      if (filterIndex >= 0) {
        if (!filter.favourite) {
          this.favouriteFilters.splice(filterIndex, 1)
        } else {
          this.favouriteFilters[filterIndex].favourite = filter.favorite
        }
      }
    },
    forgottenPassword() {
      this.forgetPasswordDialog = true
      this.resetForgetPasswordDialog++
    },
    getCurrUserFavFilters() {
      this.filtersService
        .getCurrUserFavFilters()
        .then(data => {
          

          this.$store.dispatch('commons/favoriteFilters', data)
        })
        .catch(error => {
          this.$store.dispatch('addErrorMessage', {
            severity: 'error',
            content: 'Some data was not loaded from "getCurrUserFavFilters"'
          })
        })
    },
    setupProfile(val) {
      if (val) {
        if (val.menuMode) {
          this.layoutMode = val.menuMode
        }
        if (val.menuType === 'Grouped') {
          this.grouped = true
        } else {
          this.grouped = false
        }
        if (val.menuColour === 'Dark') {
          this.darkMenu = true
        } else {
          this.darkMenu = false
        }
        // if (val.profileLogo) {
        //   this.layoutMode = val.profileLogo
        // }
      }
    },
    setupFirstLoad() {
      var names = firstLoadModules()

      names.forEach(name => {
        // 
        try {
          this.$store.dispatch(`${name}/setFirstTimeLoad`, true)
        } catch(e) {
          
        }
      })
    },
    setupMenu() {
      this.reportGroups = []

      this.allReports.forEach(report => {

        if (report && report.id && report.reportType) {
          
          let index = this.reportGroups.findIndex(a => a.label === report.reportType)

          if (index < 0) {
            this.reportGroups.push({
              label: report.reportType,
              icon: '',
              items: []
            })
            index = this.reportGroups.length - 1
          }

        
          if(report.reportName === 'Meeting Notes')
          {
            let permission = this.myPermissions.find(a => a.permission.permissionCode === 'Report-MeetingNotes');
              if (permission) {
                if (permission.permissionLevel !== 0) {
                    this.reportGroups[index].items.push({
                      label: report.reportName,
                      to: "/reports/" + report.reportName,
                      class: "submenu-item"
                    })
                } 
              }
          } 
          else if(report.reportName === 'Won Orders')
          {
            let permission = this.myPermissions.find(a => a.permission.permissionCode === 'Report-WonOrders');
              if (permission) {
                if (permission.permissionLevel !== 0) {
                    this.reportGroups[index].items.push({
                      label: report.reportName,
                      to: "/reports/" + report.reportName,
                      class: "submenu-item"
                    })
                } 
              }
          }
          else if(report.reportName === 'Quotations Stats')
          {
            let permission = this.myPermissions.find(a => a.permission.permissionCode === 'Report-FullCustomer');
              if (permission) {
                if (permission.permissionLevel !== 0) {
                    this.reportGroups[index].items.push({
                      label: report.reportName,
                      to: "/reports/" + report.reportName,
                      class: "submenu-item"
                    })
                } 
              }
          }
          else if(report.reportName === 'Lost Orders')
          {
            let permission = this.myPermissions.find(a => a.permission.permissionCode === 'Report-LostOrders');
              if (permission) {
                if (permission.permissionLevel !== 0) {
                    this.reportGroups[index].items.push({
                      label: report.reportName,
                      to: "/reports/" + report.reportName,
                      class: "submenu-item"
                    })                
                  } 
              }
          }
          else if(report.reportName === 'Outstanding Orders')
          {
            let permission = this.myPermissions.find(a => a.permission.permissionCode === 'Report-OutstandingOrders');
              if (permission) {
                if (permission.permissionLevel !== 0) { 
                    this.reportGroups[index].items.push({
                      label: report.reportName,
                      to: "/reports/" + report.reportName,
                      class: "submenu-item"
                    })                
                  } 
              }
          }
          else if(report.reportName === 'Enquiries Raised')
          {
            let permission = this.myPermissions.find(a => a.permission.permissionCode === 'Report-EnquiriesRaised');
              if (permission) {
                if (permission.permissionLevel !== 0) {
                    this.reportGroups[index].items.push({
                      label: report.reportName,
                      to: "/reports/" + report.reportName,
                      class: "submenu-item"
                    })                
                  } 
              }
          }
          else if(report.reportName === 'Enquiries not Booked')
          {
            let permission = this.myPermissions.find(a => a.permission.permissionCode === 'Report-EnquiriesNotBookedIn');
              if (permission) {
                if (permission.permissionLevel !== 0) {
                    this.reportGroups[index].items.push({
                      label: report.reportName,
                      to: "/reports/" + report.reportName,
                      class: "submenu-item"
                    })                
                  } 
              }
          }
          else if(report.reportName === 'User System Activity')
          {
            let permission = this.myPermissions.find(a => a.permission.permissionCode === 'Report-UserSystemActivity');
              if (permission) {
                if (permission.permissionLevel !== 0) {
                    this.reportGroups[index].items.push({
                      label: report.reportName,
                      to: "/reports/" + report.reportName,
                      class: "submenu-item"
                    })                
                  } 
              }
          }
          else if(report.reportName === 'Quotations Stats By Branch')
          {
            let permission = this.myPermissions.find(a => a.permission.permissionCode === 'Report-QuoteStatsBranch');
              if (permission) {
                if (permission.permissionLevel !== 0) {
                    this.reportGroups[index].items.push({
                      label: report.reportName,
                      to: "/reports/" + report.reportName,
                      class: "submenu-item"
                    })                
                  } 
              }
          }
          else if(report.reportName === 'Quotations Stats By Sales Exec')
          {
            let permission = this.myPermissions.find(a => a.permission.permissionCode === 'Report-QuoteStatsSalesExec');
              if (permission) {
                if (permission.permissionLevel !== 0) {
                    this.reportGroups[index].items.push({
                      label: report.reportName,
                      to: "/reports/" + report.reportName,
                      class: "submenu-item"
                    })                
                  } 
              }
          }
           else if(report.reportName === 'Enquiries by Customer')
           {
             let permission = this.myPermissions.find(a => a.permission.permissionCode === 'Report-EnquiriesByCustomer');
               if (permission) {
                 if (permission.permissionLevel !== 0) {
                     this.reportGroups[index].items.push({
                       label: report.reportName,
                       to: "/reports/" + report.reportName,
                       class: "submenu-item"
                     })                
                   } 
               }
           }
          else if(report.reportName === 'Sales Exec Sales Stat per Month')
          {
            let permission = this.myPermissions.find(a => a.permission.permissionCode === 'Report-SalesExecSalesStats');
              if (permission) {
                if (permission.permissionLevel !== 0) {
                    this.reportGroups[index].items.push({
                      label: report.reportName,
                      to: "/reports/" + report.reportName,
                      class: "submenu-item"
                    })                
                  } 
              }
          }
          else if(report.reportName === 'JSL Enquiries < £1,000')
          {
            let permission = this.myPermissions.find(a => a.permission.permissionCode === 'Report-JSLEnquiriesUnder1000');
              if (permission) {
                if (permission.permissionLevel !== 0) {
                    this.reportGroups[index].items.push({
                      label: report.reportName,
                      to: "/reports/" + report.reportName,
                      class: "submenu-item"
                    })                
                  } 
              }
          }
          else if(report.reportName === 'JSL Enquiries > £50,000')
          {
            let permission = this.myPermissions.find(a => a.permission.permissionCode === 'Report-JSLEnquiriesOver50000');
              if (permission) {
                if (permission.permissionLevel !== 0) {
                    this.reportGroups[index].items.push({
                      label: report.reportName,
                      to: "/reports/" + report.reportName,
                      class: "submenu-item"
                    })                
                  } 
              }
          }
          else if(report.reportName === 'JSL Enquiries 1K to 50K (Postal)')
          {
            let permission = this.myPermissions.find(a => a.permission.permissionCode === 'Report-JSLEnquiries1Kto50Kpostal');
              if (permission) {
                if (permission.permissionLevel !== 0) {
                    this.reportGroups[index].items.push({
                      label: report.reportName,
                      to: "/reports/" + report.reportName,
                      class: "submenu-item"
                    })                
                  } 
              }
          }
          else if(report.reportName === 'JSL Enquiries 1K to 50K')
          {
            let permission = this.myPermissions.find(a => a.permission.permissionCode === 'Report-JSLEnquiries1Kto50Kother');
              if (permission) {
                if (permission.permissionLevel !== 0) {
                    this.reportGroups[index].items.push({
                      label: report.reportName,
                      to: "/reports/" + report.reportName,
                      class: "submenu-item"
                    })                
                  } 
              }
          }
          else if(report.reportName === 'Duplicate Customers')
          {
            let permission = this.myPermissions.find(a => a.permission.permissionCode === 'Report-DuplicateCustomers');
              if (permission) {
                if (permission.permissionLevel !== 0) {
                    this.reportGroups[index].items.push({
                      label: report.reportName,
                      to: "/reports/" + report.reportName,
                      class: "submenu-item"
                    })
                  }
              }
          }
          else if(report.reportName === 'Duplicate Enquiries')
          {
            let permission = this.myPermissions.find(a => a.permission.permissionCode === 'Report-DuplicateEnquiries');
              if (permission) {
                if (permission.permissionLevel !== 0) {
                    this.reportGroups[index].items.push({
                      label: report.reportName,
                      // Its own page (three lists + a side-by-side comparison), not the
                      // generic db_report grid at /reports/:id.
                      to: "/duplicateEnquiries",
                      class: "submenu-item"
                    })
                  }
              }
          }
          else {
            this.reportGroups[index].items.push({
              label: report.reportName,
              to: "/reports/" + report.reportName,
              class: "submenu-item"
            })
          }


        }
      })
      
      // Branch & Product Analysis (DRAFT): admin only for now, on the widget builder's code. It sits in
      // the group that holds Enquiries Raised, since it analyses the same enquiries.
      let analysisPermission = this.myPermissions.find(a => a.permission && a.permission.permissionCode === 'Dashboard-ComposeWidgets');
      if (analysisPermission && analysisPermission.permissionLevel !== 0) {
        let raised = this.allReports.find(r => r && r.reportName === 'Enquiries Raised')
        let group = this.reportGroups.find(g => g.label === (raised && raised.reportType ? raised.reportType : 'Orders'))
        if (!group) {
          group = { label: raised && raised.reportType ? raised.reportType : 'Orders', icon: '', items: [] }
          this.reportGroups.push(group)
        }
        group.items.push({
          label: 'Branch & Product Analysis',
          to: '/branchProductAnalysis',
          class: 'submenu-item'
        })
      }

      this.reportGroups.sort((a, b) => {
        return a.label > b.label ? 1 : -1
      })


      let tmpReportGroups = [...this.reportGroups]

      for(let i=0; i<this.reportGroups.length; i++)
      {
        if(this.reportGroups[i].items.length == 0)
        {
          tmpReportGroups = tmpReportGroups.filter(rg => rg.label != this.reportGroups[i].label)
        }
      }
      
      this.reportGroups = tmpReportGroups
      
      this.menuGrouped = [
        {
          label: "MBP Group Synergy system",
          icon: "pi pi-fw pi-sitemap",
          // The name is shown as the side menu's title, above the user's name (Mohammed 04/10/2026),
          // so the section's own grey heading is not repeated.
          hideLabel: true,
          items: [
            { 
              label: "Dashboard", 
              icon: "pi pi-fw pi-home", 
              to: "/",
            },
            // {
            //   label: "Customers",
            //   icon: "pi pi-id-card",
            //   to: "/customers",
            // },
            // {
            //   label: "Contacts (old)",
            //   icon: "pi pi-id-card",
            //   to: "/contacts",
            // },
            {
              label: "Contacts",
              icon: "pi pi-id-card",
              to: "/newContacts",
            },
            // {
            //   label: "Quotations",
            //   icon: "pi pi-fw pi pi-map-marker",
            //   to: "/enquiries",
            // },
            // {
            //   label: "Quotations",
            //   icon: "pi pi-fw pi pi-map-marker",
            //   to: "/opportunities",
            // },
            // {
            //   label: "Booked Quotations",
            //   icon: "pi pi-fw fw pi-money-bill",
            //   to: "/quotations",
            // },
            {
              label: "Tasks",
              icon: "pi pi-fw pi-check-square",
              to: "/tasks",
            },
            {
              label: "Events",
              icon: "pi pi-fw pi-calendar",
              to: "/activities",
            },
            {
              label: "All Activities",
              icon: "pi pi-fw pi-list",
              to: "/allActivities",
            },
            // {
            //   label: "Activity (old)",
            //   icon: "pi pi-fw pi-calendar",
            //   to: "/myActivities",
            // },
          ],
        }
      ];



      var menuGroupedItems = [
                  {
                    label: "Actual Sales",
                    icon: "pi pi-id-card",
                    to: "/actualSales",
                  },
                  {
                    label: "Sales Forcast",
                    icon: "pi pi-id-card",
                    to: "/salesForcast",
                  }
                ]
                
      // Customers and Quotations go in next to the entry they follow, not at a fixed
      // index: the result must not depend on what else is in the menu or on the order
      // the permissions arrive in.
      const mainItems = this.menuGrouped[0].items
      const insertAfter = (to, item) => {
        const at = mainItems.findIndex(i => i.to === to)
        mainItems.splice(at === -1 ? mainItems.length : at + 1, 0, item)
      }

      this.myPermissions.forEach(permission => {
        if (permission.permission.permissionCode === 'ViewCustomers') {
          if (permission.permissionLevel !== 0) {
            insertAfter("/", {
              label: "Customers",
              icon: "pi pi-id-card",
              to: "/customers",
            })
          }
        }
        else if (permission.permission.permissionCode === 'ViewQuotations') {
          if (permission.permissionLevel !== 0) {
            insertAfter("/newContacts", {
              label: "Quotations",
              icon: "pi pi-fw pi pi-map-marker",
              to: "/enquiries"
            })
          }
        }
        else if (permission.permission.permissionCode === 'ViewReportGroup') {
          if (permission.permissionLevel !== 0) {
            this.menuGrouped.push(
              {
                label: "REPORTS",
                icon: "pi pi-fw pi-sitemap",
                items: this.reportGroups
              }
            )
          }
        }
        else if (permission.permission.permissionCode === 'Report-AllSalesStats') {
          if (permission.permissionLevel === 1) {
             menuGroupedItems.push(
                  {
                    label: "All Sales Stats",
                    icon: "pi pi-id-card",
                    to: "/salesData",
                  }
            )
          }
        }
      })
      
      this.myPermissions.forEach(permission => {
        
        if (permission.permission.permissionCode === 'ViewSettingGroup') {
          if (permission.permissionLevel === 1) {
            const isAdmin = !!(this.profile && this.profile.role && this.profile.role.roleName === 'Admin')
            this.menuGrouped.push(
              {
                label: "SETTINGS",
                icon: "pi pi-fw pi-cog",
                items: this.extendableOptions.filter(group => !group.adminOnly || isAdmin),
              }
            )
          }
        } else if (permission.permission.permissionCode === 'ViewUsersRoles') {
          if (permission.permissionLevel === 1) {
            this.menuGrouped.push(
              {
                label: "USERS & ACCESS",
                icon: "pi pi-fw pi-sitemap",
                items: [
                  // {
                  //   label: "Customer Approval",
                  //   icon: "pi pi-id-card",
                  //   to: "/customerApprovals",
                  // },
                  {
                    label: "Users",
                    icon: "pi pi-fw pi-user",
                    to: "/users",
                  },
                  {
                    label: "Roles",
                    icon: "pi pi-fw pi-user",
                    to: "/roles",
                  },
                  {
                    label: "Support Cases",
                    icon: "pi pi-fw pi-briefcase",
                    to: "/supportCases",
                  }
                ],
              }
            )
          }
        } else if (permission.permission.permissionCode === 'ViewSalesImport') {
          if (permission.permissionLevel === 1) {
            this.menuGrouped.push(
              {
                label: "SALES DATA",
                icon: "pi pi-fw pi-sitemap",
                items: menuGroupedItems
              }
            )
          }
        }
      })

      // The widget builder is no longer on the menu (Mohammed 04/10/2026): Admin opens it from the
      // "Build or edit widgets" button on Global Settings > Dashboard Settings. Its route and its
      // Dashboard-ComposeWidgets check are unchanged. My Widgets moved to the top bar's avatar menu.

      let permission = this.myPermissions.find(a => a.permission.permissionCode === 'customerApproval');
        if (permission) {
          if (permission.permissionLevel !== 0) {
              this.menuGrouped[2].items.push({
                label: "Customer Approval",
                icon: "pi pi-id-card",
                to: "/customerApprovals",
              })                
            } 
        }

      // FOLLOW UP (Mohammed 04/10/2026): one page, two tabs - Emails Going Out and Emails Sent.
      // Open to everyone, as the two entries it replaces were: the server shows each user their own
      // and Admin all. Put in last, under the main section, so the menuGrouped[2] above (Customer
      // Approval) still lands in the same section it did before.
      this.menuGrouped.splice(1, 0, {
        label: "FOLLOW UP",
        icon: "pi pi-fw pi-envelope",
        items: [
          {
            label: "Follow-up Emails",
            icon: "pi pi-fw pi-envelope",
            to: "/followUp/goingOut",
          }
        ]
      })

      // SITES / PROJECTS mock-up (Mohammed 06/10/2026): front-end pages on sample data for MBP to review,
      // last in the menu. Nothing on them is saved.
      this.menuGrouped.push({
        label: "SITES / PROJECTS",
        icon: "pi pi-fw pi-sitemap",
        // Listed here, not imported: the menu specs evaluate setupMenu() on its own. Same entries as
        // mbp/sitesProjects/pages.js, which the overview page uses.
        items: [
          { label: "How it works", icon: "pi pi-fw pi-info-circle", to: "/sitesProjects/overview" },
          { label: "New Enquiry (changed)", icon: "pi pi-fw pi-plus", to: "/sitesProjects/newEnquiry" },
          { label: "Sites", icon: "pi pi-fw pi-map-marker", to: "/sitesProjects/sites" },
          { label: "Projects", icon: "pi pi-fw pi-sitemap", to: "/sitesProjects/projects" },
          { label: "Active Enquiries (sample)", icon: "pi pi-fw pi-list", to: "/sitesProjects/activeEnquiries" },
          { label: "Projects report", icon: "pi pi-fw pi-chart-bar", to: "/sitesProjects/projectsReport" },
          { label: "Duplicate Projects", icon: "pi pi-fw pi-clone", to: "/sitesProjects/duplicates" },
          { label: "Duplicate finder: 5 scenarios", icon: "pi pi-fw pi-search", to: "/sitesProjects/finderScenarios" },
          { label: "Settings", icon: "pi pi-fw pi-cog", to: "/sitesProjects/settings" }
        ]
      })

      this.upatdeAccounts();
    },
    upatdeAccounts() {
      this.menuGrouped.map((item) => {
        if (item.label === "ACCOUNTS") {
          // if (localStorage.getItem("user-token")) {
          //   item.items = [
          //     {
          //       label: "Logout",
          //       icon: "pi pi-fw pi-sign-out",
          //       to: "/login?logout=true",
          //     },
          //   ];
          // } else {
          //   item.items = [
          //     { label: "Signup", icon: "pi pi-fw pi-user-plus", to: "/signup" },
          //     { label: "Login", icon: "pi pi-fw pi-sign-in", to: "/login" },
          //   ];
          // }
        }
      });
      this.AppMenuKey++
    },
    onDocumentClick() {
      if (!this.topbarItemClick) {
        this.activeTopbarItem = null;
        this.topbarMenuActive = false;
      }

      if (!this.menuClick) {
        if (this.isHorizontal() || this.isSlim()) {
          EventBus.$emit("reset_active_index");
          this.menuActive = false;
        }
        this.hideOverlayMenu();
      }

      if (!this.profileClick) {
        if (this.isSlim()) {
          this.profileExpanded = false;
        }
      }

      this.topbarItemClick = false;
      this.menuClick = false;
      this.profileClick = false;
    },
    onMenuButtonClick(event) {
      this.menuClick = true;
      this.topbarMenuActive = false;

      if (this.layoutMode === "overlay") {
        if (this.isDesktop()) this.overlayMenuActive = !this.overlayMenuActive;
        else this.staticMenuMobileActive = !this.staticMenuMobileActive;
      } else {
        if (this.isDesktop())
          this.staticMenuDesktopInactive = !this.staticMenuDesktopInactive;
        else this.staticMenuMobileActive = !this.staticMenuMobileActive;
      }

      event.preventDefault();
      

      setTimeout(() => {
        this.$store.dispatch('computeAppWindowWidthHeight')
      }, 300)
    },
    onTopbarMenuButtonClick(event) {
      this.topbarItemClick = true;
      this.topbarMenuActive = !this.topbarMenuActive;
      this.hideOverlayMenu();
      event.preventDefault();
    },
    onTopbarItemClick(event) {
      this.topbarItemClick = true;

      if (this.activeTopbarItem === event.item) this.activeTopbarItem = null;
      else this.activeTopbarItem = event.item;

      event.originalEvent.preventDefault();
    },
    onMenuClick() {
      this.menuClick = true;
    },
    isMenuVisible() {
      if (this.isDesktop()) {
        if (this.layoutMode === "static")
          return !this.staticMenuDesktopInactive;
        else if (this.layoutMode === "overlay") return this.overlayMenuActive;
        else return true;
      } else {
        return true;
      }
    },
    onProfileClick(event) {
      this.profileClick = true;
      this.profileExpanded = !this.profileExpanded;
      if (this.isHorizontal() || this.isSlim()) {
        EventBus.$emit("reset_active_index");
      }

      event.preventDefault();
    },
    updateUser(obj) {
      if (this.$store.getters['auth/getMyProfile']) {
        this.userService
          .updateUserById(obj, this.$store.getters['auth/getMyProfile'].id)
          .then(data => {
            this.$store.dispatch('auth/setMyProfile', {
              ...this.profile,
              ...obj
            })
          })
          .catch(error => {
            this.$toast.add({
              severity: "error",
              summary: "Error",
              detail: `Error while updating user`,
              life: 5000,
            })
          })
      }
    },
    onLayoutChange(layoutMode) {
      this.layoutMode = layoutMode;
      this.staticMenuDesktopInactive = false;
      this.overlayMenuActive = false;

      if (this.isHorizontal()) {
        this.profileMode = "popup";
      }

      let obj = {
        menuColour: this.profile.menuColour || 'Dark',
        menuType: this.profile.menuType || 'Grouped',
        menuMode: layoutMode,
        themeColour: this.profile.themeColour || 'blue',
        profileLogo: this.profile.profileLogo || 'mbp'
      }
      this.updateUser(obj)

      setTimeout(() => {
        this.$store.dispatch('computeAppWindowWidthHeight')
      }, 300)
    },
    onMenuTypeChange(menuType) {
      this.grouped = menuType;

      let obj = {
        menuColour: this.profile.menuColour || 'Dark',
        menuType: menuType ? 'Grouped' : 'Ungrouped',
        menuMode: this.profile.menuMode || 'Static',
        themeColour: this.profile.themeColour || 'blue',
        profileLogo: this.profile.profileLogo || 'mbp'
      }
      this.updateUser(obj)
    },
    onMenuColorChange(menuColor) {
      this.darkMenu = menuColor;

      let obj = {
        menuColour: menuColor ? 'Dark' : 'Light',
        menuType: this.profile.menuType || 'Grouped',
        menuMode: this.profile.menuMode || 'Static',
        themeColour: this.profile.themeColour || 'blue',
        profileLogo: this.profile.profileLogo || 'mbp'
      }
      this.updateUser(obj)
    },
    onProfileModeChange(profileMode) {
      this.profileMode = profileMode;
    },
    isHorizontal() {
      return this.layoutMode === "horizontal";
    },
    isSlim() {
      return this.layoutMode === "slim";
    },
    hideOverlayMenu() {
      this.overlayMenuActive = false;
      this.staticMenuMobileActive = false;
    },
    isDesktop() {
      return window.innerWidth > 1024;
    },
    isMobile() {
      return window.innerWidth <= 640;
    },
    onMenuItemClick(event) {
      // The breadcrumb resets only on a left-menu click (item 2, 2026-09-27) - see navTrail.js.
      if (!event.item.items && event.item.to) {
        EventBus.$emit("menu-navigation", event.item.to);
      }
      if (!event.item.items) {
        EventBus.$emit("reset_active_index");
        this.hideOverlayMenu();
      }
      if (!event.item.items && (this.isHorizontal() || this.isSlim())) {
        this.menuActive = false;
      }
    },
    onRootMenuItemClick() {
      this.menuActive = !this.menuActive;
    },

    updateAlert(alert) {
      if (alert && alert.id) {
        let alerts = [...this.alerts]
        this.alerts = []
        setTimeout(() => {
          let index = alerts.findIndex(a => a.id === alert.id)
          alerts[index] = alert
          this.alerts = alerts
          
        }, 10)
      }
    },
    editAllFavourities(filters) {
      
      this.favouriteFilters = filters
      this.unRequestedFiltersTable = false
      this.filtersDialog = true
    },
    async takeScreenshot() {
      const el = this.$refs.printMe;
      // add option type to get the image version
      // if not provided the promise will return 
      // the canvas.
      const options = {
        type: 'dataURL'
      }
      var output = await this.$html2canvas(el, options);

      var filename = 'ss.png'

      var arr = output.split(','), mime = arr[0].match(/:(.*?);/)[1],
          bstr = atob(arr[1]), n = bstr.length, u8arr = new Uint8Array(n);
      while(n--){
          u8arr[n] = bstr.charCodeAt(n);
      }

      var file = new File([u8arr], filename, {type:mime});

      let fileParams = {
        fileName: filename
      }
      try {
      let res = await this.attachmentService
        .postFile(file, fileParams)

      this.attachment = res.data
      this.supportCaseDialog = true
      }
      catch(err) {
          if (err && err.response && err.response.data) {
            if (err.response.data.includes("exceeds the configured maximum")) {
              this.$toast.add({
                severity: "error",
                summary: "File too big.",
                detail: "File size exceeds the limited size.",
                life: 5000,
              });
            }
          }
      }
    },
    viewAllAlerts() {
      this.alertsDialog = true
      this.unRequestedAlertsTable = false
      this.alerts = []

      this.requestingAlerts = true
      this.alertService
        .getAllCurrentUserAlerts()
        .then(data => {
          this.requestingAlerts = false
          this.alerts = data
        })
        .catch(error => {
          this.requestingAlerts = false

          this.$store.dispatch('addErrorMessage', {
            severity: 'error',
            content: 'Some data was not loaded from "v1/getAllCurrentUserAlerts"'
          })
        })

    },
		goto(alert) {

      this.processing = true
      
      this.note = {}
      this.customer = {}
      this.quotation = {}

			if (alert && alert.id) {
				
        if (alert.source === "ACTIVITY" || alert.source === "REMINDER") {
          this.activityService
            .getActivity(alert.sourceId)
            .then(data => {
              this.processing = false

              this.activity = data
              this.disabledArr = [
                // 'buttons',
                // 'reminderDate',
                // 'reminderTime',
                // 'reminder',
                // 'reminderType',
                // 'duration',
                // 'endDate',
                // 'endTime',
                // 'startDate',
                // 'startTime',
                // 'notes',
                // 'quotation',
                // 'customer',
                // 'contact',
                // 'activityType',
                // 'mbpBranch',
                // 'user',
                // 'dateCreated',
                // 'subject',
              ]
              this.unRequestedActivity = false
              this.activityDialog = true
              this.activityFormKey++

            })
            .catch((e) => {
              this.processing = false
                
              this.$store.dispatch('addErrorMessage', {
                severity: 'error',
                content: 'Some data was not loaded from "activity/{id}"'
              })
            });

        } else if (alert.source === "NOTE") {
          this.customerService
            .getNote(alert.sourceId)
            .then(data => {
              this.processing = false
              
              this.note = data
              // this.disabledArr = [
              //   'noteHeader',
              //   'noteTags',
              //   'Customer',
              //   'Contact',
              //   'user',
              //   'noteDetails',
              //   'buttons',
              // ]
              this.disabledArr = []
              this.unRequestedNote = false
              this.noteDialog = true
              this.reloadNoteForm++
            })
            .catch((e) => {
              this.processing = false
              
              this.$store.dispatch('addErrorMessage', {
                severity: 'error',
                content: 'Some data was not loaded from "customernote/{id}"'
              })
            });
          
        } else if (alert.source === "CUSTOMER") {
          this.customerService
            .getCustomerInfo(alert.sourceId)
            .then(data => {
              this.processing = false
              
              this.customer = data
              this.disabledArr = [
                // 'buttons',
              ]
              this.unRequestedCustomer = false
              this.customerDialog = true
            })
            .catch(err => {
              this.processing = false
              
              this.$store.dispatch('addErrorMessage', {
                severity: 'error',
                content: 'Some data was not loaded from "customerInfo"'
              })
            })
          
        } else if (alert.source === "QUOTE") {
          this.quotation = {};

          this.quotationService
            .getQuotationInfo(alert.sourceId)
            .then(data => {
              this.processing = false
              
              this.quotation = data
              this.opportunity = data.opportunity

              this.disabledArr = [
                // "opportunity",
                // "quoteVerDate",
                // "productValue",
                // "internalValue",
                // "productQty",
                // "productType",
                // "quoteGrade",
                // "expOrderDate",
                // "expectedOrderDate",
                // "reqDeliveryDate",
                // "notes",
                // "clearFile",
                // "clickButton",
                // "onFileSelect",
                // "attachDesc",
                // "hideDialog",
                // "saveQuote"
              ]
              
              this.quotationDialog = true;
            })
            .catch(err => {
              this.processing = false
              
              this.$store.dispatch('addErrorMessage', {
                severity: 'error',
                content: 'Some data was not loaded from "/v1/quotationinfo/{quoteVerNo}"'
              })
            })
          
        }
			}
		},
    getPreLoadedEps() {
      this.settingService
        .getPreLoadedEps()
        .then(data => {

          data.forEach(a => {
            // console.log(a.callEP, a.callEP.substr(0, 3), a.callEP.match(/\//g).length)
            if (
              a 
              && a.callEP
              && a.callEP.substr(0, 3) === 'v1/' 
              && a.callEP.match(/\//g).length === 1
            ) {
              this.filtersService
                .filtersCustomRequest(a.callEP)
            }
          })
        })
        .catch(error => {
          // this.requestingAlerts = false

          this.$store.dispatch('addErrorMessage', {
            severity: 'error',
            content: 'Some data was not loaded from "v1/getAllCurrentUserAlerts"'
          })
        })
    }
  },
  computed: {
    myPermissions() {
      return this.$store.getters['auth/getUserPermissions']
    },
    loginDialog() {
      return this.$store.getters.reLoginDialog
    },
    route() {
      return this.$route.name
    },
    profile() {
      return this.$store.getters['auth/getMyProfile']
    },
    router() {
      return this.$route.path;
    },
    containerClass() {
      return [
        "layout-wrapper",
        {
          "layout-horizontal": this.layoutMode === "horizontal",
          "layout-overlay": this.layoutMode === "overlay",
          "layout-static": this.layoutMode === "static",
          "layout-slim": this.layoutMode === "slim",
          "layout-static-inactive": this.staticMenuDesktopInactive,
          "layout-mobile-active": this.staticMenuMobileActive,
          "layout-overlay-active": this.overlayMenuActive,
          "layout-menu-dark": this.darkMenu,
          "layout-menu-light": !this.darkMenu,
          "p-input-filled": this.$appState.inputStyle === "filled",
          "p-ripple-disabled": this.$primevue.ripple === false,
        },
      ];
    },
    displayInlineProfile() {
      return this.profileMode === "inline" && this.layoutMode !== "horizontal";
    },
    
    activityFormKey: {
      set() {
        this.activityFormKey1++
      },
      get() {
        return 'activityFormKey-' + this.activityFormKey1.toString()
      }
    },
    activityForm() {
      return (this.unRequestedActivity === false || this.activityDialog) ? ActivityForm : null
    },
    noteForm() {
      return (this.unRequestedNote === false || this.noteDialog) ? NoteForm : null
    },
    customerForm() {
      return (this.unRequestedCustomer === false || this.customerDialog) ? customerForm : null
    },
    quotationForm() {
      return (this.unRequestedQuotation === false || this.quotationDialog) ? QuotationForm : null
    },
    alertsTable() {
      return (this.unRequestedAlertsTable === false || this.alertsDialog) ? AlertsTable : null
    },
    filtersTable() {
      return (this.unRequestedFiltersTable === false || this.filtesrDialog) ? FiltersTable : null
    },
    loadingAlerts() {
      if (this.alerts && this.alerts[0]) {
        return false
      } else {
        if (this.requestingAlerts) {
          return true
        } else {
          return false
        }
      }
    }
  },
  components: {
    AppTopBar: AppTopBar,
    AppConfig: AppConfig,
    AppInlineProfile: AppInlineProfile,
    AppMenu: AppMenu,
    AppBreadcrumb: AppBreadcrumb,
    AppFooter: AppFooter,
    LoginForm,
    SupportCaseForm,
    ForgetPassword,
    ActivityForm,
    NoteForm,
    AlertsTable,
    FiltersTable,
    customerForm,
    QuotationForm,
    FollowupRevisionDialog,
    AnomalyConfirmDialog
  },
};
</script>

<style lang="scss">
@import url('https://cdn.syncfusion.com/ej2/material.css');
@import "App.scss";

#progressDiv {
  position: fixed;
  bottom: 10px;
  right: 150px;
  width: 300px;
  height: 70px;
  background-color: aliceblue;
  border-radius: 10px;
  text-align: center;
}

#progressDetails {
  padding: 15px 5px;
}

.clearer {
  color:blue;
}

.anchorTag {
  cursor: pointer;
}

.flex {
  display: flex;
}

.justify_space_between {
  justify-content: space-between;
}

.align_items_center {
  align-items: center;
}

.under_lined {
  cursor: pointer;
  text-decoration: underline;
  color: blue;
}

/* A dashboard widget that opens its report when clicked (item 4, decision 2a). Only a widget
   with somewhere to go carries this, so the pointer is the affordance. */
.dashboard-drillable {
  cursor: pointer;
}

h5.dashboard-drillable {
  text-decoration: underline;
}

.pointer {
  cursor: pointer;
  font-size: 18px;
}

.switch {
  display: flex;
  justify-content: space-between;
  align-items: center;

  h5 {
    margin: auto;
  }
}

.login-body .login-content #login-quote {
  color: white;
  width: 40%;
  position: absolute;
  bottom: 12%;
  right: 5%;
  font-size: 1.6rem;
  font-family: Lucida Calligraphy;
  text-align: justify;

  strong {
    font-size: 1.6rem;
  }
  
  @media (max-width: 48rem) {
    display: none;
  }
}

.layout-wrapper.layout-menu-dark .layout-menu-container .layout-menu li a .layout-menuitem-text {
  color: #ffff;
  font-weight: bolder;
}

a.submenu-item.p-ripple .layout-menuitem-text {
  color: #BDBDBD!important;
}

</style>
